import { isNil } from 'ramda';
import videojs, { VideoJsPlayer } from 'video.js';

import PlayerError, { ErrorMessage } from '@src/errors/PlayerError';
import { Event } from '@src/types/event';
import type { SourceOption, VideoId, VideoObj } from '@src/types/player';
import { prepareAdTagUrl } from '@src/utils/ads';
import { getAndParseVcpInfo } from '@src/utils/vcp';

import Base from '../core/Base';

// TODO may consider move to vjsPlayer.controller as vjs plugin instead of on proxy player level
class ControlPlayerMixin extends Base {
  public async setSourceWithAdTag(
    vjsPlayer: VideoJsPlayer,
    source: SourceOption,
    adTagOptions?: {
      adTagUrl?: string;
      adsResponse?: string;
      adUnitCode?: string;
      adSize?: string;
      adCustomParams?: string;
      adDescriptionUrl?: string;
    },
    otherOptions?: {
      blockAd?: boolean;
      resetAdsContentSrc?: boolean;
      startTime?: number;
    }
  ) {
    const {
      blockAd,
      resetAdsContentSrc = false,
      startTime,
    } = otherOptions ?? {};
    const { options_ } = vjsPlayer;
    const { video, videoId } = source;

    const adTagUrl = prepareAdTagUrl(adTagOptions);
    const { adsResponse } = adTagOptions ?? {};

    // clean player error if previously had any
    if (vjsPlayer.error()) {
      vjsPlayer.error(null);
    }

    const parsedInfo =
      video || videoId
        ? await getAndParseVcpInfo({
            videoIdOrObject: (video as VideoObj) || (videoId as VideoId),
            mediaServerURL: options_.mediaServerURL,
            onError: options_.onError,
          })
        : null;

    if (!parsedInfo?.src) {
      options_.onError?.(
        new PlayerError(
          ErrorMessage.FAIL_TO_GET_SRC_OF_PARSED_VCP_INFO_FROM_VIDEO_OBJECT_OR_VIDEOID,
          {
            cause: {
              ...(video && { video }),
              ...(videoId && { videoId }),
            },
          }
        ),
        null
      );
      return;
    }

    if (!isNil(blockAd)) {
      vjsPlayer.contextPlugin.setState({ blockAd });
    }

    const shouldBlockAd = vjsPlayer.contextPlugin.state.blockAd;

    const isSourceChanged = parsedInfo.src !== vjsPlayer.ads?.contentSrc;

    const floatingInfo = vjsPlayer.getChild('FloatingInfo');

    if (floatingInfo) {
      floatingInfo.updateDomContent({
        title: parsedInfo.title,
        description: parsedInfo.description,
      });
    }

    vjsPlayer?.poster(parsedInfo?.poster || '');

    if (vjsPlayer.adsManagerPlugin?.getImaInstance() && !shouldBlockAd) {
      // ads manager loaded event wont be fired properly on next adsLoader.requestAds when
      // current ad break is still being played (especially VMAP), await resetting IMA controller complete
      // before calling setContentWithAdTag
      if (vjsPlayer.adsManagerPlugin?.isAdPlaying()) {
        await vjsPlayer.adsManagerPlugin.resetIma();
      }

      if (!isSourceChanged || resetAdsContentSrc) {
        vjsPlayer.adsManagerPlugin.resetContribAdsContentSrc();
      }

      if (!vjsPlayer.paused()) {
        vjsPlayer.pause();
      }

      vjsPlayer.adsManagerPlugin.setContentWithAdTagOptions(parsedInfo, {
        adTagUrl,
        adsResponse,
      });

      vjsPlayer.off(Event.PLAY, vjsPlayer.adsManagerPlugin.requestImaAds); // remove previous listener
      vjsPlayer.one(Event.PLAY, vjsPlayer.adsManagerPlugin.requestImaAds);
    } else {
      if (!isSourceChanged) {
        vjsPlayer.trigger(Event.X_SOURCENOTCHANGED);
      }

      // AdsManagerPlugin has already handled blockAd for next video,
      // but here still can skip request ads if we already know have to blockAd anyway
      this.changeSrc(vjsPlayer, parsedInfo as videojs.Tech.SourceObject);
    }

    vjsPlayer.eventManagerPlugin.resetStateForNextSource();

    if (!isNil(startTime)) {
      const handleReadyToSeek = () => {
        vjsPlayer.off(['loadedmetadata', 'canplay'], handleReadyToSeek);
        this.seekContentToTime(vjsPlayer, startTime);
      };

      vjsPlayer.one(['loadedmetadata', 'canplay'], handleReadyToSeek);
    }
  }

  public changeSrc(
    vjsPlayer: VideoJsPlayer,
    contentSrc: string | videojs.Tech.SourceObject | videojs.Tech.SourceObject[]
  ) {
    // Only try to pause the player when initialised with a source already
    if (vjsPlayer.currentSrc()) {
      vjsPlayer.currentTime(0);
      vjsPlayer.pause();
    }
    if (contentSrc) {
      vjsPlayer.src(contentSrc);
    }
    vjsPlayer.one('loadedmetadata', () => this.seekContentToTime(vjsPlayer, 0));
  }

  public seekContentToTime(vjsPlayer: VideoJsPlayer, time: number) {
    vjsPlayer.contextPlugin.setState({ initialStartTime: time || 0 });
    vjsPlayer.currentTime(time);
  }

  public toast(
    vjsPlayer: VideoJsPlayer,
    message: string,
    options?: { duration: number }
  ) {
    const toastDisplay = vjsPlayer.getChild('ToastDisplay');

    if (toastDisplay) {
      toastDisplay.toast(message, options);
    }
  }
}

export default ControlPlayerMixin;
